import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, EmptyState, SearchField } from '@skupreme/shared/ui';
import { useOrgSearch } from '../api/orgs';
import type { OrgSummary } from '../api/types';
import { DataTable, type DataColumn } from '../components/DataTable';
import { QueryView } from '../components/QueryView';
import { StatusBadge } from '../components/StatusBadge';
import { Timestamp } from '../components/Timestamp';

const COLUMNS: DataColumn<OrgSummary>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: org => (
      <Link
        to={`/orgs/${encodeURIComponent(org.id)}`}
        className="sk:font-medium sk:text-link"
      >
        {org.name}
      </Link>
    ),
  },
  { id: 'id', header: 'ID', cell: org => org.id },
  { id: 'products', header: 'Products', cell: org => org.products.join(', ') },
  {
    id: 'status',
    header: 'Status',
    cell: org => <StatusBadge status={org.status} />,
  },
  {
    id: 'created',
    header: 'Created',
    cell: org => <Timestamp value={org.created_at} />,
  },
];

export function OrgSearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q')?.trim() ?? '';
  const [text, setText] = useState(q);
  const search = useOrgSearch(q);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setParams(text.trim() ? { q: text.trim() } : {});
  };

  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      <h1 className="sk:text-lg sk:font-semibold">Organizations</h1>
      <form onSubmit={submit} className="sk:flex sk:gap-2">
        <SearchField
          aria-label="Search organizations"
          placeholder="Name, id, legacy id, member email or division"
          value={text}
          onChange={setText}
          className="sk:w-96"
        />
        <Button type="submit" variant="primary">
          Search
        </Button>
      </form>
      {q === '' ? (
        <EmptyState title="Search for an organization" />
      ) : (
        <QueryView query={search}>
          {data => (
            <DataTable
              label="Organizations"
              columns={COLUMNS}
              rows={data.orgs}
              getKey={org => org.id}
              empty={`No organizations match "${q}"`}
            />
          )}
        </QueryView>
      )}
    </div>
  );
}
